/**
 * Voice engine catalog — lets an app register the dictation engines it can
 * offer (the browser's native service, on-device models of different sizes
 * and accuracy) so a UI can present the choice to the user, and wires the
 * chosen engine's provider.
 *
 * Register the catalog once at startup with `registerVoiceEngines()`; a
 * picker reads it via `listVoiceEngines()` and applies the user's choice
 * with `selectVoiceEngine(id)` (which bonds the engine's provider).
 *
 * @module
 */
import { setProvider } from './provider.js';
let engines = [];
let selectedId = null;
/**
 * Registers the app's dictation engine catalog (replaces any previous one).
 * @param defs - The engines to offer, in display order.
 */
export function registerVoiceEngines(defs) {
    engines = defs;
}
/**
 * Returns the registered dictation engine catalog (empty when the app
 * offers no choice).
 * @returns The engines in display order.
 */
export function listVoiceEngines() {
    return engines;
}
/**
 * Selects an engine by id: bonds its provider (via `setProvider`) and
 * remembers the selection.
 * @param id - The engine id to select.
 * @returns The selected engine, or null when the id is not registered.
 */
export function selectVoiceEngine(id) {
    const def = engines.find((e) => e.id === id) ?? null;
    if (!def)
        return null;
    selectedId = id;
    setProvider(def.create());
    return def;
}
/**
 * Returns the currently selected engine id, or null when none was selected.
 * @returns The selected engine id.
 */
export function getSelectedVoiceEngineId() {
    return selectedId;
}
/**
 * Checks whether an engine covers a BCP-47 language tag.
 * @param def - The engine to check.
 * @param language - BCP-47 tag (e.g. 'en-US').
 * @returns True when the engine can transcribe the language.
 */
export function voiceEngineCoversLanguage(def, language) {
    if (def.languages === 'all')
        return true;
    return def.languages.includes(language.split('-')[0].toLowerCase());
}
